import React, { useState, useCallback } from 'react';
import CharacterSelect from './components/CharacterSelect';
import GameScreen from './components/GameScreen';
import ResultScreen from './components/ResultScreen';
import LoginScreen from './components/LoginScreen';
import SettingsModal from './components/SettingsModal';
import MenuScreen from './components/MenuScreen';
import CharacterCreateScreen from './components/creation/CharacterCreateScreen';
import FamilySelectScreen from './components/creation/FamilySelectScreen';
import { Act1Screen } from './components/act1/Act1Screen';
import { Act1ResultScreen } from './components/act1/Act1ResultScreen';
import { Act2SceneSelect } from './components/act2/Act2SceneSelect';
import { Act2Screen } from './components/act2/Act2Screen';
import { Act2ResultScreen } from './components/act2/Act2ResultScreen';
import { Act3Screen } from './components/act3/Act3Screen';
import { Act3ResultScreen } from './components/act3/Act3ResultScreen';
import { useGameStore } from './stores/gameStore';
import { useRootStore } from './stores/rootStore';
import { useAct1Store } from './stores/act1Store';
import { useAuth } from './hooks/useAuth';
import { Settings } from 'lucide-react';
import { generateCharacter, generateFamily } from './services/api/v2GameApi';
import type { CharacterInput } from '@shared/types/character';
import type { FamilyMemberInput } from '@shared/types/family';

const V1_STORAGE_KEY = 'cyber-matchmaker-game';
const V2_STORAGE_KEYS = ['cm-v2-root', 'cm-v2-act1', 'cm-v2-act2', 'cm-v2-act3'];

const App = () => {
  const { loggedIn, loading, loggingIn, login, logout } = useAuth();

  // v2 stores
  const v2 = useRootStore();
  const act1 = useAct1Store();

  // v1 store (kept for backward compat during transition)
  const v1 = useGameStore();

  // Settings modal state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Determine which version to use based on v2 step
  const useV2 = v2.step !== 'MENU' || v1.step === 'MENU';

  const handleClearSave = useCallback(() => {
    localStorage.removeItem(V1_STORAGE_KEY);
    V2_STORAGE_KEYS.forEach(k => localStorage.removeItem(k));
    v1.goToMenu();
    v2.resetAll();
  }, [v1, v2]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const handleGoToMenu = useCallback(() => {
    v1.goToMenu();
    v2.resetAll();
  }, [v1, v2]);

  // v2: Character creation complete → generate full characters via LLM
  const handleCharacterCreateComplete = useCallback(async (inputA: CharacterInput, inputB: CharacterInput) => {
    setIsGenerating(true);
    try {
      const [charA, charB] = await Promise.all([
        generateCharacter(inputA),
        generateCharacter(inputB),
      ]);
      v2.setProtagonists(charA, charB);
      v2.goToFamilySelect();
    } catch (err) {
      console.error('Failed to generate characters:', err);
      // Fallback: create characters without LLM enrichment
      const fallbackChar = (input: CharacterInput, gender: 'male' | 'female') => ({
        id: `custom_${gender}_${Date.now()}`,
        ...input,
        avatar: `https://picsum.photos/seed/${Math.floor(Math.random() * 100)}/200/200`,
        bio: `${input.name}，${input.age}岁${input.job}`,
        speakingStyle: '正常说话',
        hiddenTraits: [],
        dealbreakers: [],
      });
      v2.setProtagonists(fallbackChar(inputA, 'male'), fallbackChar(inputB, 'female'));
      v2.goToFamilySelect();
    } finally {
      setIsGenerating(false);
    }
  }, [v2]);

  // v2: Family selection complete → generate full family members via LLM
  const handleFamilyComplete = useCallback(async (
    familyAInputs: FamilyMemberInput[],
    familyBInputs: FamilyMemberInput[],
  ) => {
    if (!v2.protagonistA || !v2.protagonistB) return;
    setIsGenerating(true);
    try {
      const [membersA, membersB] = await Promise.all([
        generateFamily(familyAInputs, v2.protagonistA),
        generateFamily(familyBInputs, v2.protagonistB),
      ]);
      v2.setFamily(membersA, membersB);

      // Initialize Act 1 store
      act1.initialize(v2.protagonistA, v2.protagonistB, membersA, membersB);
      v2.startAct1();
    } catch (err) {
      console.error('Failed to generate family:', err);
    } finally {
      setIsGenerating(false);
    }
  }, [v2, act1]);

  // Auth gate
  if (!loggedIn) {
    return <LoginScreen loading={loading} loggingIn={loggingIn} onLogin={login} />;
  }

  // Determine current step for settings modal
  const currentStep = useV2 ? v2.step : v1.step;

  return (
    <div className="min-h-screen w-full bg-cream text-[#2D2D2D] font-sans selection:bg-coral-100">
      {/* ── v2 Routes ─────────────────────────────────────── */}

      {v2.step === 'MENU' && v1.step === 'MENU' && (
        <MenuScreen onStart={() => v2.goToCharacterCreate()} />
      )}

      {v2.step === 'CHARACTER_CREATE' && (
        <CharacterCreateScreen
          onComplete={handleCharacterCreateComplete}
          isGenerating={isGenerating}
        />
      )}

      {v2.step === 'FAMILY_SELECT' && v2.protagonistA && v2.protagonistB && (
        <FamilySelectScreen
          protagonistA={v2.protagonistA}
          protagonistB={v2.protagonistB}
          onComplete={handleFamilyComplete}
          isGenerating={isGenerating}
        />
      )}

      {v2.step === 'ACT1_GAME' && v2.protagonistA && v2.protagonistB && (
        <Act1Screen onSettingsClick={() => setSettingsOpen(true)} />
      )}

      {v2.step === 'ACT1_RESULT' && (
        <Act1ResultScreen />
      )}

      {v2.step === 'ACT2_SETUP' && (
        <Act2SceneSelect />
      )}

      {v2.step === 'ACT2_GAME' && v2.protagonistA && v2.protagonistB && (
        <Act2Screen onSettingsClick={() => setSettingsOpen(true)} />
      )}

      {v2.step === 'ACT2_RESULT' && (
        <Act2ResultScreen />
      )}

      {v2.step === 'ACT3_GAME' && v2.protagonistA && v2.protagonistB && (
        <Act3Screen onSettingsClick={() => setSettingsOpen(true)} />
      )}

      {v2.step === 'ACT3_RESULT' && (
        <Act3ResultScreen />
      )}

      {/* ── v1 Routes (backward compat) ───────────────────── */}

      {v2.step === 'MENU' && v1.step === 'SELECT' && (
        <CharacterSelect onSelect={v1.startGame} />
      )}

      {v2.step === 'MENU' && v1.step === 'GAME' && v1.charA && v1.charB && v1.firstSpeaker && (
        <GameScreen onSettingsClick={() => setSettingsOpen(true)} />
      )}

      {v2.step === 'MENU' && v1.step === 'RESULT' && v1.charA && v1.charB && v1.gameResult && (
        <ResultScreen />
      )}

      {/* ── Global settings button ────────────────────────── */}
      {!['GAME'].includes(v1.step) && !['ACT1_GAME', 'ACT2_GAME', 'ACT3_GAME'].includes(v2.step) && (
        <button
          onClick={() => setSettingsOpen(true)}
          className="fixed top-3 right-3 z-40 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm border border-[#E5E0D8] shadow-md hover:shadow-lg hover:bg-white transition-all text-[#9CA3AF] hover:text-[#6B7280]"
          aria-label="设置"
        >
          <Settings size={16} />
        </button>
      )}

      {/* Settings modal */}
      <SettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        currentStep={currentStep}
        onGoToMenu={handleGoToMenu}
        onClearSave={handleClearSave}
        onLogout={handleLogout}
      />
    </div>
  );
};

export default App;
